import "@/App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import CookieConsent from "@/components/CookieConsent";
import AppGate from "@/components/AppGate";
import WorkspaceRoute from "@/components/WorkspaceRoute";
import RouteSeo from "@/components/RouteSeo";
import PageLoading from "@/components/PageLoading";
import ScrollToTop from "@/components/ScrollToTop";
import { useLocale } from "@/i18n/useLocale";

const Layout = lazy(() => import("@/components/Layout"));
const Legal = lazy(() => import("@/pages/Legal"));
const Landing = lazy(() => import("@/pages/Landing"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const PublicTools = lazy(() => import("@/pages/PublicTools"));
const PublicTool = lazy(() => import("@/pages/PublicTool"));
const EditorialTeam = lazy(() => import("@/pages/EditorialTeam"));
const AuthorProfile = lazy(() => import("@/pages/AuthorProfile"));
const Contacto = lazy(() => import("@/pages/Contacto"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const FirebaseAction = lazy(() => import("@/pages/FirebaseAction"));
const AdvisorPanel = lazy(() => import("@/pages/AdvisorPanel"));
const DiagnosisWelcome = lazy(() => import("@/pages/DiagnosisWelcome"));
const DiagnosisWizard = lazy(() => import("@/pages/DiagnosisWizard"));
const DiagnosisPreparing = lazy(() => import("@/pages/DiagnosisPreparing"));
const DiagnosisResult = lazy(() => import("@/pages/DiagnosisResult"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Paywall = lazy(() => import("@/pages/Paywall"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const MiProyecto = lazy(() => import("@/pages/MiProyecto"));
const Itinerario = lazy(() => import("@/pages/Itinerario"));
const TramiteDetalle = lazy(() => import("@/pages/TramiteDetalle"));
const Asistente = lazy(() => import("@/pages/Asistente"));
const Documentos = lazy(() => import("@/pages/Documentos"));
const PreparaTuCita = lazy(() => import("@/pages/PreparaTuCita"));
const Facturacion = lazy(() => import("@/pages/Facturacion"));
const Perfil = lazy(() => import("@/pages/Perfil"));
const Notificaciones = lazy(() => import("@/pages/Notificaciones"));
const Opiniones = lazy(() => import("@/pages/Opiniones"));
const ContactoAsesor = lazy(() => import("@/pages/ContactoAsesor"));
const Admin = lazy(() => import("@/pages/Admin"));

function Protected({ children }) {
  const { user, loading, t } = useApp();
  if (loading) return <PageLoading label={t("common.loading")} />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading, t } = useApp();
  if (loading) return <PageLoading label={t("common.loading")} />;
  if (!user || user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function LocalizedApp() {
  useLocale();
  return (
    <div className="App">
      <BrowserRouter>
          <ScrollToTop />
          <RouteSeo />
          <Suspense fallback={<PageLoading label={i18nLoading()} />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/en" element={<Landing />} />
            <Route path="/precios" element={<Pricing />} />
            <Route path="/en/pricing" element={<Pricing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/en/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/en/blog/:slug" element={<BlogArticle />} />
            <Route path="/herramientas" element={<PublicTools />} />
            <Route path="/en/tools" element={<PublicTools />} />
            <Route path="/herramientas/calculadora-coste-sl" element={<PublicTool toolKey="slCost" />} />
            <Route path="/en/tools/sl-company-cost-calculator" element={<PublicTool toolKey="slCost" />} />
            <Route path="/herramientas/autonomo-vs-sl-vs-sa" element={<PublicTool toolKey="legalComparison" />} />
            <Route path="/en/tools/self-employed-vs-sl-vs-sa" element={<PublicTool toolKey="legalComparison" />} />
            <Route path="/herramientas/calculador-cuota-autonomos" element={<PublicTool toolKey="autonomoFee" />} />
            <Route path="/en/tools/self-employed-contribution-calculator" element={<PublicTool toolKey="autonomoFee" />} />
            <Route path="/herramientas/cronologia-crear-sl" element={<PublicTool toolKey="slTimeline" />} />
            <Route path="/en/tools/sl-company-formation-timeline" element={<PublicTool toolKey="slTimeline" />} />
            <Route path="/equipo-editorial" element={<EditorialTeam />} />
            <Route path="/en/editorial-team" element={<EditorialTeam />} />
            <Route path="/autora" element={<AuthorProfile />} />
            <Route path="/en/author" element={<AuthorProfile />} />
            <Route path="/soporte" element={<Contacto />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/login" element={<Login />} />
            <Route path="/registro" element={<Register />} />
            <Route path="/recuperar" element={<ForgotPassword />} />
            <Route path="/restablecer" element={<ResetPassword />} />
            <Route path="/verificar" element={<VerifyEmail />} />
            <Route path="/auth/action" element={<FirebaseAction />} />
            <Route path="/panel-asesor" element={<Protected><AdvisorPanel /></Protected>} />
            <Route path="/diagnostico" element={<DiagnosisWelcome />} />
            <Route path="/en/diagnosis" element={<DiagnosisWelcome />} />
            <Route path="/diagnostico/preguntas" element={<DiagnosisWizard />} />
            <Route path="/diagnostico/preparando" element={<DiagnosisPreparing />} />
            <Route path="/diagnostico/resultado" element={<DiagnosisResult />} />
            <Route path="/pago" element={<Paywall />} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/app" element={<AppGate><Layout /></AppGate>}>
              <Route index element={<Dashboard />} />
              <Route path="proyecto" element={<MiProyecto />} />
              <Route path="itinerario" element={<Itinerario />} />
              <Route path="tramite/:pid" element={<TramiteDetalle />} />
              <Route path="asistente" element={<WorkspaceRoute capability="ai_chat"><Asistente /></WorkspaceRoute>} />
              <Route path="documentos" element={<WorkspaceRoute capability="view_documents_page"><Documentos /></WorkspaceRoute>} />
              <Route path="prepara-tu-cita" element={<WorkspaceRoute capability="prepare_appt"><PreparaTuCita /></WorkspaceRoute>} />
              <Route path="facturacion" element={<Facturacion />} />
              <Route path="perfil" element={<Perfil />} />
              <Route path="notificaciones" element={<Notificaciones />} />
              <Route path="opiniones" element={<WorkspaceRoute capability="share_feedback"><Opiniones /></WorkspaceRoute>} />
              <Route path="ayuda" element={<WorkspaceRoute capability="get_help"><Opiniones feedbackType="technical" /></WorkspaceRoute>} />
              <Route path="contacto" element={<WorkspaceRoute capability="get_help"><ContactoAsesor /></WorkspaceRoute>} />
              <Route path="contacto-asesor" element={<Navigate to="/app/contacto" replace />} />
              <Route path="admin" element={<AdminOnly><Admin /></AdminOnly>} />
              <Route path="asesores" element={<Navigate to="/app" replace />} />
              <Route path="asesorias" element={<Navigate to="/app" replace />} />
              <Route path="asesores/:aid" element={<Navigate to="/app" replace />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <CookieConsent />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

function i18nLoading() {
  return document.documentElement.lang === "en" ? "Loading…" : "Cargando…";
}

function App() {
  return <AppProvider><LocalizedApp /></AppProvider>;
}

export default App;
